import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { AlertCircle, Plus, Eye, Clock, Search, Grid, List, Globe, FileText, Phone } from 'lucide-react';
import { Box, Button, TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSettings } from '../contexts/SettingsContext';
import LeadDetailsModal from './LeadDetailsModal';
import LeadTable from './LeadTable';
import trakLogo from '../assets/trak_logo.svg';
import appleLogo from '../assets/apple_logo.png';
import androidLogo from '../assets/android_logo.png';
import windowsLogo from '../assets/windows_logo.png';
import './KanbanBoard.css';

const SortableLeadCard = ({ lead, isOverThreshold, thresholds, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0
    };

    const locationText = lead.suburb || '';

    // Helper function to get device icon/logo based on device type
    const getDeviceDisplay = (deviceString) => {
        if (!deviceString) {
            return { icon: <Globe size={16} color="#ba68c8" />, isImage: false };
        }

        const device = deviceString.toLowerCase();
        if (device.includes('iphone') || device.includes('ipad') || device.includes('ios') || device.includes('mac os')) {
            return { logo: appleLogo, alt: 'Apple Device', isImage: true };
        } else if (device.includes('android')) {
            return { logo: androidLogo, alt: 'Android Device', isImage: true };
        } else if (device.includes('windows')) {
            return { logo: windowsLogo, alt: 'Windows Device', isImage: true };
        } else {
            return { icon: <Globe size={16} color="#ba68c8" />, isImage: false };
        }
    };

    const deviceDisplay = getDeviceDisplay(lead.Device);

    return (
        <div ref={setNodeRef} style={style} className={`quote-card ${isOverThreshold ? 'over-threshold' : ''} ${lead.isHighValue ? 'high-value' : ''} ${isDragging ? 'dragging' : ''}`} {...attributes}>
            <div className="quote-header" {...listeners}>
                <span className="quote-id">
                    {lead.simpro_quote && lead.simpro_quote.toString().trim() !== '' && lead.simpro_quote !== 'null' && lead.simpro_quote !== 'undefined' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <img
                                src={trakLogo}
                                alt="Trak"
                                style={{ width: '50px', height: '20px' }}
                            />
                            <span>JobID: {lead.simpro_quote}</span>
                        </div>
                    ) : `L${lead.id}`}
                </span>
                <div>
                    {lead.isHighValue && <span className="high-value-indicator" title="High Value Lead">💎</span>}
                    {isOverThreshold && <span className="threshold-indicator"><AlertCircle size={16} /></span>}
                </div>
            </div>

            <div className="quote-customer">{lead.customer}</div>

            <div className="quote-tags">
                {lead.phone && (
                    <span className="tag tag-phone" title={`Phone: ${lead.phone}`}>
                        <Phone size={12} />
                        <span style={{ marginLeft: '4px' }}>{lead.phone}</span>
                    </span>
                )}
                {lead.Device && (
                    <span className="tag tag-device" title={lead.Device}>
                        {deviceDisplay.isImage ? (
                            <img
                                src={deviceDisplay.logo}
                                alt={deviceDisplay.alt}
                                style={{
                                    width: 16,
                                    height: 16,
                                    objectFit: 'contain'
                                }}
                            />
                        ) : (
                            deviceDisplay.icon
                        )}
                    </span>
                )}
                {locationText && <span className="tag tag-location" title={locationText}>{locationText}</span>}
                {lead.page_view_count > 0 && <span className="tag tag-views" title={`Page Views: ${lead.page_view_count}`}>{lead.page_view_count} views</span>}
                {(lead.service_type || lead.requestType) && (
                    <span
                        className={`tag ${(lead.service_type || lead.requestType || '').toLowerCase().includes('get an estimate') ? 'tag-service-estimate' :
                            (lead.service_type || lead.requestType || '').toLowerCase().includes('online') ? 'tag-service-online' :
                                (lead.service_type || lead.requestType || '').toLowerCase().includes('onsite') ? 'tag-service-onsite' :
                                    'tag-service'
                            }`}
                        title={`Service: ${lead.service_type || lead.requestType}`}
                    >
                        {lead.service_type || lead.requestType}
                    </span>
                )}
            </div>

            <div className="quote-footer">
                <div className="days-in-status">
                    <Clock size={14} />
                    <span>{lead.daysInStatus} days</span>
                </div>
                {lead.latestUpdate && <div className="latest-update">{format(new Date(lead.latestUpdate), 'MMM d')}</div>}
            </div>

            {isOverThreshold && (
                <div className="threshold-alert">
                    <AlertCircle size={14} />
                    <span>Exceeds {thresholds.getThresholdForStatus ? thresholds.getThresholdForStatus(lead.status) : 0} day threshold</span>
                </div>
            )}

            <button
                className="details-button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    console.log('View details button clicked for lead:', lead.id);
                    onClick(lead);
                }}
            >
                <Eye size={14} style={{ marginRight: '4px' }} />
                View Details
            </button>
        </div>
    );
};

const LeadKanbanBoard = ({ leads = [], logs = [], onStatusChange, onAddLead, onAddLog, onUpdateLead, activeFilter = null }) => {
    const { getThresholdForStatus, isLeadOverThreshold, isHighValueLead } = useSettings();
    const [columns, setColumns] = useState({});
    const [activeId, setActiveId] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'table'
    const [filteredLeads, setFilteredLeads] = useState([]);
    const dialogRef = useRef(null);

    // Updated status categories for lead management
    const statusCategories = [
        'Online Quotes',
        'Onsite Quotes',
        'Price Estimates',
        'Drop Off'
    ];

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        // Filter leads based on search term and active filter
        let filtered = leads;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = leads.filter(lead => {
                const fullName = `${lead.Customer_name || ''} ${lead.Customer_surname || ''}`.trim();
                return (
                    fullName.toLowerCase().includes(searchLower) ||
                    (lead.email || lead.email_address || '').toLowerCase().includes(searchLower) ||
                    (lead.phone || lead.mobileNumber || '').toLowerCase().includes(searchLower) ||
                    (lead.product || lead.Product || '').toLowerCase().includes(searchLower) ||
                    (lead.service_type || lead.requestType || '').toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply active filter
        if (activeFilter) {
            filtered = filtered.filter(lead => {
                switch (activeFilter) {
                    case 'onsite_bookings':
                        return lead.status === 'Onsite Quotes';
                    case 'online_bookings':
                        return lead.status === 'Online Quotes';
                    case 'price_estimates':
                        return lead.status === 'Price Estimates';
                    case 'price_estimate_conversions':
                        return lead.status === 'Price Estimates' && lead.simpro_quote;
                    case 'dropoffs':
                        return lead.status === 'Drop Off';
                    case 'total_leads':
                        return true; // All leads
                    case 'iphone_views':
                        return lead.Device && lead.Device.toLowerCase().includes('iphone');
                    case 'android_views':
                        return lead.Device && lead.Device.toLowerCase().includes('android');
                    case 'pc_views':
                        return lead.Device && !lead.Device.toLowerCase().includes('mobile') && !lead.Device.toLowerCase().includes('iphone') && !lead.Device.toLowerCase().includes('android');
                    case 'page_views':
                        return lead.page_view_count > 0;
                    default:
                        return true;
                }
            });
        }

        setFilteredLeads(filtered);

        // Update columns for kanban view
        const initialColumns = {};
        statusCategories.forEach(status => { initialColumns[status] = []; });

        filtered.forEach(lead => {
            const status = lead.status || 'Drop Off';

            if (initialColumns[status]) {
                const daysInStatus = lead.daysInStatus || 0;
                const threshold = getThresholdForStatus(status);
                const isOverThreshold = isLeadOverThreshold(lead);

                initialColumns[status].push({
                    ...lead,
                    daysInStatus,
                    isOverThreshold,
                    isHighValue: isHighValueLead(lead),
                    latestUpdate: lead.last_page_view || lead.date_issued
                });
            }
        });

        setColumns(initialColumns);
    }, [leads, searchTerm, activeFilter]);

    const [loadingLead, setLoadingLead] = useState(false);

    const handleLeadClick = async (lead) => {
        console.log('handleLeadClick called with lead:', lead.id);

        setLoadingLead(true);
        try {
            const res = await fetch(`/api/leads/${lead.id}`);
            const fullLead = await res.json();
            setSelectedLead(fullLead);
            setModalOpen(true);
        } catch (error) {
            console.error('Failed to fetch full lead:', error);
        } finally {
            setLoadingLead(false);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) { setActiveId(null); return; }

        let sourceColumnId;
        let lead;
        for (const status of statusCategories) {
            const leadIndex = columns[status]?.findIndex(l => l.id === active.id);
            if (leadIndex >= 0) {
                sourceColumnId = status;
                lead = columns[status][leadIndex];
                break;
            }
        }

        if (!sourceColumnId || !lead) { setActiveId(null); return; }

        const destinationEl = over.data.current?.sortable?.containerId || sourceColumnId;

        if (sourceColumnId !== destinationEl) {
            const newSourceColumn = columns[sourceColumnId].filter(l => l.id !== active.id);
            const newDestColumn = [...(columns[destinationEl] || []), { ...lead, status: destinationEl, daysInStatus: 0, isOverThreshold: false }];
            setColumns({ ...columns, [sourceColumnId]: newSourceColumn, [destinationEl]: newDestColumn });

            if (onStatusChange) { onStatusChange(active.id, destinationEl); }
        }
        setActiveId(null);
    };

    // Function to handle exporting leads to Excel
    const handleExportToExcel = () => {
        // Flatten all leads from all columns into a single array
        const allLeads = [];
        Object.keys(columns).forEach(status => {
            columns[status].forEach(lead => {
                // Add the category/status to each lead
                allLeads.push({
                    ...lead,
                    category: status
                });
            });
        });

        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Convert leads array to worksheet
        const worksheet = XLSX.utils.json_to_sheet(allLeads);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Create a Blob from the buffer
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        // Save the file
        saveAs(blob, `eclipse_dash_leads_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="kanban-board">
            {/* Standalone Export Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileText size={16} />}
                    onClick={handleExportToExcel}
                    sx={{ fontWeight: 'bold', padding: '10px 20px', fontSize: '16px' }}
                >
                    Export All Leads to Excel
                </Button>
            </Box>

            {/* Search and Controls */}
            <Box sx={{ mb: 3, px: 2 }}>
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'flex-start',
                    mb: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ flexGrow: 1, minWidth: 300, maxWidth: '70%' }}>
                        <TextField
                            placeholder="Search by name, email, phone, or product..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#2a2a2a',
                                    color: '#e0e0e0',
                                    '& fieldset': { borderColor: '#444' },
                                    '&:hover fieldset': { borderColor: '#666' },
                                    '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                                },
                                '& .MuiInputBase-input::placeholder': { color: '#9e9e9e' }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={20} style={{ color: '#9e9e9e' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Results Summary - moved to align with search bar */}
                        <Typography variant="body2" sx={{ color: '#9e9e9e', mt: 1, ml: 1 }}>
                            {searchTerm ? (
                                `Found ${filteredLeads.length} lead${filteredLeads.length !== 1 ? 's' : ''} matching "${searchTerm}"`
                            ) : (
                                `Showing all ${leads.length} lead${leads.length !== 1 ? 's' : ''}`
                            )}
                        </Typography>
                    </Box>

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: '#e0e0e0',
                                borderColor: '#444',
                                '&.Mui-selected': {
                                    bgcolor: '#1976d2',
                                    color: '#ffffff',
                                    '&:hover': { bgcolor: '#1565c0' }
                                },
                                '&:hover': { bgcolor: '#2a2a2a' }
                            }
                        }}
                    >
                        <ToggleButton value="kanban" aria-label="kanban view">
                            <Grid size={16} style={{ marginRight: 4 }} />
                            Kanban
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                            <List size={16} style={{ marginRight: 4 }} />
                            Table
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* View Content */}
            {viewMode === 'table' ? (
                <LeadTable leads={filteredLeads} onLeadClick={handleLeadClick} />
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
                    <div className="kanban-columns">
                        {statusCategories.map(status => (
                            <div key={status} className="kanban-column" data-status={status}>
                                <div className="column-header">
                                    <h3>{status}</h3>
                                    <span className="quote-count">{columns[status]?.length || 0}</span>
                                    {columns[status]?.filter(l => l.isOverThreshold).length > 0 &&
                                        <span className="alert-badge"><AlertCircle size={16} /></span>
                                    }
                                </div>

                                <div className="column-content" data-column={status}>
                                    <SortableContext items={columns[status]?.map(l => l.id) || []} strategy={verticalListSortingStrategy} id={status}>
                                        {columns[status]?.map(lead => (
                                            <SortableLeadCard
                                                key={lead.id}
                                                lead={lead}
                                                isOverThreshold={lead.isOverThreshold}
                                                thresholds={{ getThresholdForStatus }}
                                                onClick={handleLeadClick}
                                            />
                                        ))}
                                    </SortableContext>
                                </div>
                            </div>
                        ))}
                    </div>

                    <DragOverlay>
                        {activeId && <div className="quote-card dragging">Dragging...</div>}
                    </DragOverlay>
                </DndContext>
            )}

            {loadingLead && <div className="loading-spinner">Loading...</div>}

            {modalOpen && selectedLead && (
                <LeadDetailsModal
                    open={modalOpen}
                    lead={selectedLead}
                    onClose={() => { setModalOpen(false); setTimeout(() => setSelectedLead(null), 100); }}
                />
            )}
        </div>
    );
};

export default LeadKanbanBoard;
